import type { Node } from "@mapcraft-tasks/client-store";
import {
	useBacklinks,
	useBreadcrumbs,
	useDispatch,
	useIsPinned,
	useNode,
	usePlacementsOf,
	useStore,
	useSyncStatus
} from "@mapcraft-tasks/client-store/react";
import { isSystemRole, SYSTEM_NODE_IDS } from "@mapcraft-tasks/domain";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useToggleDone } from "../lib/complete";
import { usePhone } from "../lib/mobile";
import { useOutlineEngine } from "../outline/controller";
import { Checkbox } from "./checkbox";
import { DayNav, useGoToDay } from "./day-nav";
import { ActionRail } from "./mobile/action-rail";
import { chordHints, ModeBar } from "./mode-bar";
import { Outline } from "./outline/outline";
import { RenderedTitle } from "./outline/rendered-title";
import { TitleEditor } from "./outline/title-editor";

/**
 * Единственный экран системы — страница ноды.
 *
 * Today, Inbox, страница проекта, страница дня — не разные экраны, а эта же страница
 * с другим корнем. Одна ветка рендера вместо пяти; любая нода бесплатно получает
 * полноценную страницу (ui-spec Область 1).
 */
export function NodePage({
	rootId,
	title,
	subtitle,
	ensureDate,
	day
}: {
	rootId: string;
	title?: string;
	subtitle?: string;
	/** Для страницы дня: дата, которую надо завести первой же записью. */
	ensureDate?: string;
	/** Для страницы дня: дата, по соседям которой листает шапка. */
	day?: string;
}) {
	const navigate = useNavigate();
	const phone = usePhone();
	const node = useNode(rootId);
	const crumbs = useBreadcrumbs(rootId);
	const status = useSyncStatus();
	const [inspector, setInspector] = useState(false);
	const [titleEditing, setTitleEditing] = useState(false);
	const toggleDone = useToggleDone();
	const goToDay = useGoToDay();
	/* Листалка положена любой странице дня, а не только `/today` и `/day/<дата>`: в day-ноду
	   ходят и по прямой ссылке `/n/<id>` — из бэклинков и поиска. Дата тогда берётся
	   из самой ноды. */
	const dayDate = day ?? (node?.role === "day" ? node.dayDate : null);
	/* Правится только собственный заголовок обычной ноды: у страниц-алиасов («Сегодня»,
	   «Входящие») он не из ноды вовсе, а системные ноды принадлежат системе. Роль
	   спрашиваем `isSystemRole`, а не перечислением по месту — так `pinned` однажды
	   и оказался вне защиты (domain/types.ts). */
	const titleEditable = title === undefined && node !== undefined && !isSystemRole(node.role);

	const engine = useOutlineEngine({
		rootId,
		onZoom: (nodeId) => void navigate({ to: "/n/$nodeId", params: { nodeId } }),
		onGo: (to) => void navigate({ to }),
		ensureDate
	});

	return (
		<div className="panel flex h-full min-w-0 flex-1 flex-col">
			<header className="page-header shrink-0 px-4 pt-4 pb-2 md:px-8 md:pt-6 md:pb-3">
				{crumbs.length > 0 && (
					<nav className="mb-1 flex flex-wrap items-baseline gap-[3px] text-xs font-light text-ink-muted">
						{crumbs.map((crumb) => (
							<span key={crumb.id} className="flex items-baseline gap-[3px]">
								<Link
									to="/n/$nodeId"
									params={{ nodeId: crumb.id }}
									className="transition-colors hover:text-ink-strong"
								>
									{labelOf(crumb.id, crumb.title)}
								</Link>
								<span className="text-ink-faint">/</span>
							</span>
						))}
					</nav>
				)}

				<div className="flex items-baseline gap-3">
					{/*
					 * Галочка провалившейся ноды. Провалиться внутрь задачи — обычный способ
					 * с ней работать (подзадачи, заметка), и отмечать её оттуда человек
					 * должен там же, где видит: иначе за галочкой надо подниматься к родителю.
					 * Компонент общий со строкой аутлайна — «сделал» везде значит одно и то же.
					 */}
					{node && (
						<Checkbox
							node={node}
							size={20}
							className="self-center"
							title="выполнено"
							onToggle={() => toggleDone(node)}
						/>
					)}
					{/*
					 * В правке заголовок занимает строку целиком: у редактора нет собственной ширины,
					 * он тянется по контейнеру. Вне правки — по содержимому, чтобы дата и пин стояли
					 * вплотную к названию.
					 *
					 * Клик висит на самом `h1`, а не на кнопке вокруг текста: внутри заголовка живут
					 * кнопки меншенов, а кнопку в кнопку вкладывать нельзя.
					 */}
					<h1
						className={`min-w-0 text-2xl font-bold tracking-tight text-ink-strong md:text-3xl ${
							titleEditing ? "flex-1" : "truncate"
						} ${titleEditable ? "cursor-text" : ""}`}
						onClick={titleEditable ? () => setTitleEditing(true) : undefined}
					>
						{title ??
							(node ? (
								<PageTitle
									node={node}
									editing={titleEditing}
									onDone={() => setTitleEditing(false)}
									onOpenMention={(id) => void navigate({ to: "/n/$nodeId", params: { nodeId: id } })}
								/>
							) : (
								"…"
							))}
					</h1>
					{/*
					 * Дата и листалка — одна мета-группа, а не два соседа заголовка. Группа
					 * стоит на базовой линии `h1` (по своему первому элементу — дате), а внутри
					 * себя центрирует всё по этой же мелкой строке. Иначе листалка равнялась бы
					 * по высокой строке заголовка и висела бы заметно выше даты.
					 */}
					{(subtitle || dayDate) && (
						<span className="flex shrink-0 items-center gap-2 self-baseline">
							{subtitle && <span className="tabular text-sm text-ink-muted">{subtitle}</span>}
							{dayDate && <DayNav date={dayDate} onDate={goToDay} />}
						</span>
					)}
					{node && <PinMark nodeId={node.id} />}
					<button
						type="button"
						className="ml-auto flex shrink-0 items-baseline gap-2 rounded-sm px-2 py-px text-xs font-light text-ink-muted transition-colors hover:bg-active hover:text-ink-strong"
						onClick={() => setInspector((v) => !v)}
					>
						инспектор <span className="kbd max-md:hidden">ii</span>
					</button>
				</div>
			</header>

			{/*
			 * Щелчок мимо списка снимает выделение — поэтому обработчик висит на всей области
			 * вокруг аутлайна, а не на самом списке: «мимо» это и поля по бокам, и пустота под
			 * последней строкой. Оверлей исключён целиком: под ним список закрыт, и щелчок по
			 * затемнению значит «закрой меня», а не «сними выделение» — иначе команда, ради
			 * которой оверлей открыли, осталась бы без строк.
			 */}
			<div
				className="page-scroll flex min-h-0 flex-1 flex-col overflow-x-hidden overflow-y-auto px-4 md:px-8"
				onMouseDown={(e) => {
					// Именно `Element`, а не `HTMLElement`: внутри строки живут SVG (галочка,
					// шеврон), а они HTML-элементами не являются — с той проверкой щелчок по
					// галочке считался бы промахом мимо списка и снимал выделение.
					const target = e.target instanceof Element ? e.target : null;
					if (engine.state.overlay !== null || target?.closest(".row")) {
						return;
					}
					engine.actions.clearSelection();
				}}
			>
				{/*
				 * Ширина не ограничена. Мера строки имеет смысл там, где строка ПЕРЕНОСИТСЯ:
				 * узкая колонка тогда бережёт глаз на возврате к началу следующей строки.
				 * Здесь заголовок обрезается многоточием — колонка не берегла чтение, а просто
				 * отрезала текст, пока справа пустовал экран.
				 */}
				<div className="shrink-0">
					{status.loading ? <Skeleton /> : <Outline engine={engine} />}
					{inspector && node && <Inspector nodeId={node.id} />}
				</div>
				{/*
				 * Хвост живёт ЗДЕСЬ, а не внутри аутлайна: он обязан быть соседом списка
				 * в самом скроллере — только так пустота под последней строкой умеет
				 * растягиваться и не давать лишней прокрутки (`.scroll-tail`). Заодно он
				 * остаётся «мимо списка» для щелчка выше: клик по пустоте снимает выделение.
				 */}
				<div className="scroll-tail" aria-hidden="true" />
			</div>

			{/*
			 * Внизу панели всегда одна и та же полоса, но говорит она на разных языках:
			 * с клавиатурой — про режим и аккорды, под пальцем — самими кнопками.
			 * Место и роль общие, содержимое — по способу ввода.
			 */}
			{phone ? (
				<ActionRail engine={engine} />
			) : (
				<ModeBar state={engine.state} hints={chordHints(engine.state.chord)} />
			)}
		</div>
	);
}

/**
 * Заголовок страницы — это заголовок самой ноды, и правится он здесь же: провалившись
 * внутрь, человек не должен подниматься к родителю ради переименования. Редактор тот же,
 * что в строке аутлайна, — значит меншены и `[[`-автокомплит работают и тут.
 */
function PageTitle({
	node,
	editing,
	onDone,
	onOpenMention
}: {
	node: Node;
	editing: boolean;
	onDone: () => void;
	onOpenMention: (id: string) => void;
}) {
	const dispatch = useDispatch();
	const store = useStore();

	if (editing) {
		return (
			<TitleEditor
				value={node.title}
				caret="end"
				placeholder="без названия"
				onCommit={(raw) => {
					if (raw !== node.title) {
						dispatch({
							name: "renameNode",
							input: {
								nodeId: node.id,
								title: raw,
								baseContentVersion: store.contentVersionOf(node.id)
							}
						});
					}
				}}
				onCancel={onDone}
				onKeyDown={(e) => {
					// `Enter` здесь только выходит из правки: соседа заголовку страницы заводить негде.
					if (e.key === "Enter") {
						e.preventDefault();
						e.currentTarget.blur();
					}
				}}
			/>
		);
	}

	if (node.title.trim() === "") {
		return <span className="text-ink-faint">без названия</span>;
	}
	return <RenderedTitle text={node.title} onOpenMention={onOpenMention} />;
}

export function labelOf(id: string, title: string): string {
	if (id === SYSTEM_NODE_IDS.root) {
		return "все";
	}
	if (id === SYSTEM_NODE_IDS.inbox) {
		return "входящие";
	}
	if (id === SYSTEM_NODE_IDS.calendar) {
		return "календарь";
	}
	return title;
}

/** Системные контейнеры-вплетения показываются человеческим словом, а не служебным title. */
function containerLabel(container: { id: string; title: string }): string {
	if (container.id === SYSTEM_NODE_IDS.pinned) {
		return "пины";
	}
	return container.title;
}

function PinMark({ nodeId }: { nodeId: string }) {
	const pinned = useIsPinned(nodeId);
	return pinned ? (
		<span className="shrink-0 text-link" title="в пинах">
			◆
		</span>
	) : null;
}

/**
 * Инспектор — редкое и объёмное: бэклинки и «где ещё показывается». Частое (дата, статус,
 * роль) ставится клавиатурой прямо в строке, фокус из аутлайна не уходит.
 */
function Inspector({ nodeId }: { nodeId: string }) {
	const backlinks = useBacklinks(nodeId);
	const placements = usePlacementsOf(nodeId);

	return (
		<section className="panel-dim mt-8 mb-12 grid gap-6 p-5 sm:grid-cols-2">
			<div>
				<div className="section-label mb-2">Упоминается в</div>
				{backlinks.length === 0 ? (
					<p className="text-sm font-light text-ink-muted">пока нигде</p>
				) : (
					<ul>
						{backlinks.map((node) => (
							<li key={node.id}>
								<Link
									to="/n/$nodeId"
									params={{ nodeId: node.id }}
									className="-mx-2 block truncate rounded-sm px-2 py-px text-sm transition-colors hover:bg-active"
								>
									{node.title}
								</Link>
							</li>
						))}
					</ul>
				)}
			</div>
			<div>
				<div className="section-label mb-2">Где ещё показывается</div>
				{placements.length === 0 ? (
					<p className="text-sm font-light text-ink-muted">только дома</p>
				) : (
					<ul>
						{placements.map(({ placementId, container }) => (
							<li key={placementId}>
								<Link
									to="/n/$nodeId"
									params={{ nodeId: container.id }}
									className="-mx-2 block truncate rounded-sm px-2 py-px text-sm text-accent transition-colors hover:bg-active"
								>
									↗ {containerLabel(container)}
								</Link>
							</li>
						))}
					</ul>
				)}
			</div>
		</section>
	);
}

/**
 * Первый старт грузит снапшот — стор пуст не потому, что пусто, а потому что рано.
 * Показать в этот момент «ничего нет» значило бы соврать.
 */
function Skeleton() {
	return (
		<ul className="space-y-2 py-4">
			{[80, 60, 70, 45, 65].map((width, i) => (
				<li
					key={width}
					className="h-3 animate-pulse rounded-sm bg-panel-dim"
					style={{ width: `${width}%`, animationDelay: `${i * 90}ms` }}
				/>
			))}
		</ul>
	);
}
